import { z } from "zod";
import { buildSystemPrompt, type AssistantContext, type AssistantRequest, type AssistantResponse, type ChatMessage } from "@otrack/commands/assistant";
import {
  applyOperations, canBeBillable, clientCurrencies, createInvoiceDraft, entryDuration, entryEarnings, formatMoney, installmentPlanError, invoiceDocument, isEntryLocked, isLive,
  isOverdue, liveInvoice, logEntry, makeId, markInvoicePaid, nextInvoiceNumber, outstandingByCurrency, overlappingEntries, projectCurrency, rateSnapshotFor,
  runningEntry, sendChecks, sendInvoice, setInstallments, sortCurrencies, startTimer, stopTimer, taskLabel, uninvoicedByCurrency, type Dataset, type Invoice, type MoneyByCurrency,
  type OperationFactory, type SyncOperation, createEvent, type RepeatRule
} from "@otrack/domain";
import { billingSummary } from "./billing-labels";
import type { GoogleView } from "./calendar/google";
import { projectLabel, projectRefs } from "./commands";
import { findInvoices, openTasks, plannedEvents, uninvoicedWork } from "./lookups";
import { formatClock, formatDuration, formatPeriod, HOUR, lastMonth, MINUTE, startOfDay } from "./time";

export type AssistantCall = (request: AssistantRequest) => Promise<AssistantResponse>;

/**
 * What the assistant looked at to answer, kept as data so the Command Bar can show the records
 * themselves under the reply. Only the last lookup of each kind is kept.
 */
export type AssistantSource =
  | { kind: "entries"; ids: string[]; total: number }
  | { kind: "summary"; groupBy: "project" | "client" | "task" | "day"; from: number; to: number; rows: Array<{ key: string; label: string; ms: number; money: MoneyByCurrency }> }
  | { kind: "invoices"; ids: string[]; total: number }
  | { kind: "uninvoiced"; clients: Array<{ clientId: string; name: string; money: MoneyByCurrency; ms: number; oldest: number | null }> }
  | { kind: "events"; total: number; items: Array<{ key: string; title: string; start: number; end: number; allDay: boolean; projectId: string | null; recurring: boolean; logged: number; calendar: string | null }> }
  | { kind: "tasks"; total: number; items: Array<{ id: string; name: string; projectId: string; logged: number; estimate: number | null }> }
  | { kind: "projects"; total: number; items: Array<{ id: string; openTasks: number; tracked: number }> };

/** A step the assistant is taking, named after the tool it called; "compose" while it writes the reply. */
export type AssistantStep = ToolName | "compose";

export interface AssistantOutcome {
  answer: string;
  sources: AssistantSource[];
  operations: SyncOperation[];
  /** One line per change, for the Undo Toast or Preview Card. */
  changes: string[];
  /** Bulk or destructive changes wait for the User to confirm. */
  needsConfirmation: boolean;
}

const MAX_ROUNDS = 6;

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const timeSchema = z.string().regex(/^\d{1,2}:\d{2}$/);
const rangeSchema = z.object({ from: dateSchema, to: dateSchema });
const schemas = {
  find_entries: rangeSchema.extend({ projectId: z.string().optional(), text: z.string().optional() }),
  summarize: rangeSchema.extend({ groupBy: z.enum(["project", "client", "task", "day"]) }),
  log_time: z.object({ projectId: z.string(), taskId: z.string().optional(), date: dateSchema, start: timeSchema, end: timeSchema, description: z.string().max(1000).optional() }),
  start_timer: z.object({ projectId: z.string().optional(), description: z.string().max(1000).optional() }),
  stop_timer: z.object({}).passthrough(),
  update_entries: z.object({ entryIds: z.array(z.string()).min(1).max(200), projectId: z.string().optional(), description: z.string().max(1000).optional(), billable: z.boolean().optional() }),
  delete_entries: z.object({ entryIds: z.array(z.string()).min(1).max(200) }),
  list_invoices: z.object({ status: z.enum(["draft", "sent", "paid", "unpaid", "overdue"]).optional(), clientId: z.string().optional() }),
  uninvoiced_work: z.object({ clientId: z.string().optional() }),
  draft_invoice: z.object({ clientId: z.string(), from: dateSchema.optional(), to: dateSchema.optional(), currency: z.string().optional() }),
  split_invoice: z.object({ invoiceId: z.string(), percents: z.array(z.number().int()).min(1).max(5) }),
  mark_invoice_sent: z.object({ invoiceId: z.string(), issueDate: dateSchema.optional() }),
  mark_invoice_paid: z.object({ invoiceId: z.string(), paidDate: dateSchema.optional() }),
  find_events: rangeSchema,
  list_tasks: z.object({ projectId: z.string().optional() }),
  plan_event: z.object({
    title: z.string().max(200),
    date: dateSchema,
    start: timeSchema.optional(),
    end: timeSchema.optional(),
    endDate: dateSchema.optional(),
    projectId: z.string().optional(),
    repeat: z.enum(["daily", "weekdays", "weekly", "monthly"]).optional(),
    repeatCount: z.number().int().min(1).max(999).optional(),
    notes: z.string().max(4000).optional()
  })
};
export type ToolName = keyof typeof schemas;

function localDate(value: string): number {
  const [y = 1970, m = 1, d = 1] = value.split("-").map(Number);
  return new Date(y, m - 1, d).getTime();
}

function localTime(day: number, value: string): number {
  const [h = 0, m = 0] = value.split(":").map(Number);
  return day + h * HOUR + m * MINUTE;
}

const isoDay = (ts: number) => { const d = new Date(ts); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export function assistantContext(dataset: Dataset, now: number): AssistantContext {
  const running = runningEntry(dataset);
  const day = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(now);
  return {
    localNow: `${day}, ${isoDay(now)} ${formatClock(now)}`,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    running: running ? `${projectLabel(dataset, running.projectId)} since ${formatClock(running.start)}${running.description ? ` (${running.description})` : ""}` : null,
    projects: projectRefs(dataset).map((ref) => {
      const project = dataset.projects[ref.id]!;
      return {
        id: ref.id,
        name: ref.name,
        client: ref.clientName,
        billing: billingSummary(dataset, project),
        currency: projectCurrency(dataset, project),
        tasks: (ref.tasks ?? []).slice(0, 30).map((task) => ({ id: task.id, name: taskLabel(dataset, dataset.tasks[task.id]!) }))
      };
    }),
    clients: Object.values(dataset.clients)
      .filter((client) => isLive(client) && client.archivedAt === null)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((client) => ({ id: client.id, name: client.name, currencies: clientCurrencies(dataset, client.id) }))
  };
}

/** Executes tools against the device's data. Changes accumulate and are only returned, not applied. */
class ToolRunner {
  operations: SyncOperation[] = [];
  changes: string[] = [];
  touched = new Set<string>();
  mutationCalls = 0;
  deletes = 0;
  /** Drafting and sending invoices always wait for the User to confirm. */
  confirm = false;
  private looked = new Map<AssistantSource["kind"], AssistantSource>();

  get sources(): AssistantSource[] {
    return [...this.looked.values()];
  }

  private saw(source: AssistantSource) {
    // Re-adding moves a kind to the end, so the newest lookup is shown last.
    this.looked.delete(source.kind);
    this.looked.set(source.kind, source);
  }

  constructor(private dataset: Dataset, private readonly factory: OperationFactory, private readonly now: number, private readonly google?: GoogleView) {}

  run(name: string, rawArguments: string): unknown {
    if (!(name in schemas)) return { error: `Unknown tool ${name}` };
    let parsedJson: unknown;
    try { parsedJson = JSON.parse(rawArguments || "{}"); } catch { return { error: "Arguments were not valid JSON" }; }
    const parsed = schemas[name as ToolName].safeParse(parsedJson);
    if (!parsed.success) return { error: `Invalid arguments: ${parsed.error.issues.map((issue) => `${issue.path.join(".")} ${issue.message}`).join("; ")}` };
    const args = parsed.data as Record<string, unknown>;
    switch (name as ToolName) {
      case "find_entries": return this.find(args as z.infer<typeof schemas.find_entries>);
      case "summarize": return this.summarize(args as z.infer<typeof schemas.summarize>);
      case "log_time": return this.log(args as z.infer<typeof schemas.log_time>);
      case "start_timer": return this.start(args);
      case "stop_timer": return this.stop();
      case "update_entries": return this.update(args as z.infer<typeof schemas.update_entries>);
      case "delete_entries": return this.remove(args as z.infer<typeof schemas.delete_entries>);
      case "list_invoices": return this.listInvoices(args);
      case "uninvoiced_work": return this.uninvoiced(args);
      case "draft_invoice": return this.draftInvoice(args as z.infer<typeof schemas.draft_invoice>);
      case "split_invoice": return this.splitInvoice(args as z.infer<typeof schemas.split_invoice>);
      case "mark_invoice_sent": return this.markSent(args as z.infer<typeof schemas.mark_invoice_sent>);
      case "mark_invoice_paid": return this.markPaid(args as z.infer<typeof schemas.mark_invoice_paid>);
      case "find_events": return this.findEvents(args as z.infer<typeof schemas.find_events>);
      case "list_tasks": return this.listTasks(args);
      case "plan_event": return this.planEvent(args as z.infer<typeof schemas.plan_event>);
    }
  }

  private commit(operations: SyncOperation[], change: string, entryIds: string[]): { ok: true; done: string } {
    this.operations.push(...operations);
    this.dataset = applyOperations(this.dataset, operations);
    this.changes.push(change);
    this.mutationCalls += 1;
    for (const id of entryIds) this.touched.add(id);
    return { ok: true, done: change };
  }

  private range(from: string, to: string) {
    return { from: localDate(from), to: startOfDay(localDate(to), 1) };
  }

  private liveProject(id: string | undefined) {
    if (!id) return undefined;
    const project = this.dataset.projects[id];
    return project && isLive(project) ? project : null;
  }

  private find(args: z.infer<typeof schemas.find_entries>) {
    const { from, to } = this.range(args.from, args.to);
    const text = args.text?.toLowerCase();
    const entries = Object.values(this.dataset.timeEntries)
      .filter((e) => isLive(e) && e.start >= from && e.start < to && (!args.projectId || e.projectId === args.projectId) && (!text || e.description.toLowerCase().includes(text)))
      .sort((a, b) => b.start - a.start);
    this.saw({ kind: "entries", ids: entries.slice(0, 100).map((e) => e.id), total: entries.length });
    return {
      total: entries.length,
      entries: entries.slice(0, 100).map((e) => {
        const earned = entryEarnings(e, this.now);
        return {
          id: e.id, date: isoDay(e.start), start: formatClock(e.start), end: e.end === null ? "running" : formatClock(e.end),
          duration: formatDuration(entryDuration(e, this.now)), project: projectLabel(this.dataset, e.projectId), projectId: e.projectId,
          task: e.taskId ? this.taskName(e.taskId) : null, description: e.description, billable: e.billable,
          earned: earned ? formatMoney(earned) : null, invoiced: isEntryLocked(this.dataset, e)
        };
      })
    };
  }

  private taskName(taskId: string): string | null {
    const task = this.dataset.tasks[taskId];
    return task ? taskLabel(this.dataset, task) : null;
  }

  private summarize(args: z.infer<typeof schemas.summarize>) {
    const { from, to } = this.range(args.from, args.to);
    const groups = new Map<string, { key: string; label: string; ms: number; billableMs: number; money: Record<string, number> }>();
    const add = (key: string, label: string, ms: number, billableMs: number, money: { amount: number; currency: string } | null) => {
      const group = groups.get(key) ?? { key, label, ms: 0, billableMs: 0, money: {} };
      group.ms += ms;
      group.billableMs += billableMs;
      if (money) group.money[money.currency] = (group.money[money.currency] ?? 0) + money.amount;
      groups.set(key, group);
    };
    const keyFor = (projectId: string | null, taskId: string | null, ts: number): [string, string] => {
      const project = projectId ? this.dataset.projects[projectId] : undefined;
      switch (args.groupBy) {
        case "project": return [projectId ?? "none", projectLabel(this.dataset, projectId)];
        case "client": { const client = project?.clientId ? this.dataset.clients[project.clientId] : undefined; return [client?.id ?? "none", client?.name ?? "No client"]; }
        case "task": return [taskId ?? "none", taskId ? this.taskName(taskId) ?? "Unknown task" : "No task"];
        case "day": return [isoDay(ts), isoDay(ts)];
      }
    };
    for (const e of Object.values(this.dataset.timeEntries)) {
      if (!isLive(e) || e.start < from || e.start >= to) continue;
      const ms = entryDuration(e, this.now);
      const [key, label] = keyFor(e.projectId, e.taskId, e.start);
      add(key, label, ms, e.billable ? ms : 0, entryEarnings(e, this.now));
    }
    for (const task of Object.values(this.dataset.tasks)) {
      const project = this.dataset.projects[task.projectId];
      if (!isLive(task) || !project || project.billingMode !== "fixed" || task.fixedPrice === null || task.doneAt === null || task.doneAt < from || task.doneAt >= to) continue;
      const [key, label] = keyFor(project.id, task.id, task.doneAt);
      add(key, label, 0, 0, { amount: task.fixedPrice, currency: projectCurrency(this.dataset, project) });
    }
    for (const project of Object.values(this.dataset.projects)) {
      if (!isLive(project) || project.billingMode !== "fixed" || project.fixedPrice === null || project.completedAt === null || project.completedAt < from || project.completedAt >= to) continue;
      const [key, label] = keyFor(project.id, null, project.completedAt);
      add(key, label, 0, 0, { amount: project.fixedPrice, currency: projectCurrency(this.dataset, project) });
    }
    const totals: Record<string, number> = {};
    const sorted = [...groups.values()].sort((a, b) => b.ms - a.ms);
    this.saw({ kind: "summary", groupBy: args.groupBy, from, to, rows: sorted.map(({ key, label, ms, money }) => ({ key, label, ms, money })) });
    const rows = sorted.map((group) => {
      for (const [currency, amount] of Object.entries(group.money)) totals[currency] = (totals[currency] ?? 0) + amount;
      return { group: group.label, tracked: formatDuration(group.ms), billable: formatDuration(group.billableMs), earned: Object.entries(group.money).map(([currency, amount]) => formatMoney({ amount, currency })) };
    });
    return { rows, totalTracked: formatDuration([...groups.values()].reduce((sum, g) => sum + g.ms, 0)), totalEarned: Object.entries(totals).map(([currency, amount]) => formatMoney({ amount, currency })) };
  }

  /** Planned Events with what was logged from each, so plan and actual can be compared. */
  private findEvents(args: z.infer<typeof schemas.find_events>) {
    const { from, to } = this.range(args.from, args.to);
    const source = plannedEvents(this.dataset, from, to, this.now, this.google);
    const plannedMs = source.items.filter((item) => !item.allDay).reduce((sum, item) => sum + item.end - item.start, 0);
    const items = source.items.slice(0, 150);
    this.saw({ ...source, items });
    return {
      total: source.total,
      events: items.map((item) => ({
        title: item.title, date: isoDay(item.start), allDay: item.allDay,
        start: item.allDay ? null : formatClock(item.start), end: item.allDay ? null : formatClock(item.end),
        project: item.projectId ? projectLabel(this.dataset, item.projectId) : null, repeats: item.recurring, ...(item.calendar ? { googleCalendar: item.calendar } : {}),
        planned: item.allDay ? null : formatDuration(item.end - item.start), logged: item.logged ? formatDuration(item.logged) : null
      })),
      totalPlanned: formatDuration(plannedMs),
      totalLoggedFromPlans: formatDuration(items.reduce((sum, item) => sum + item.logged, 0))
    };
  }

  private planEvent(args: z.infer<typeof schemas.plan_event>) {
    const project = this.liveProject(args.projectId);
    if (project === null) return { error: "Unknown project id" };
    const day = localDate(args.date);
    const allDay = !args.start;
    let start = day, end = startOfDay(args.endDate ? localDate(args.endDate) : day, 1);
    if (!allDay) {
      start = localTime(day, args.start!);
      end = args.end ? localTime(day, args.end) : start + HOUR;
      if (end <= start) end += 24 * HOUR;
    }
    const weekday = new Date(start).getDay();
    const repeat: RepeatRule | null = !args.repeat ? null : {
      freq: args.repeat === "daily" ? "daily" : args.repeat === "monthly" ? "monthly" : "weekly",
      interval: 1,
      weekdays: args.repeat === "weekdays" ? [1, 2, 3, 4, 5] : args.repeat === "weekly" ? [weekday] : [],
      until: null,
      count: args.repeatCount ?? null
    };
    const created = createEvent({
      title: args.title.trim(), notes: args.notes ?? "", projectId: project?.id ?? null, taskId: null, allDay, start, end,
      startDay: allDay ? isoDay(start) : null, endDay: allDay ? isoDay(end - 1) : null, repeat
    }, this.factory);
    const when = allDay ? (args.endDate && args.endDate !== args.date ? `${args.date} to ${args.endDate}` : `${args.date}, all day`) : `${args.date} ${formatClock(start)} to ${formatClock(end)}`;
    return this.commit(created.operations, `Plan ${args.title.trim() || "an event"}, ${when}${repeat ? `, repeating ${args.repeat}` : ""}`, [created.id]);
  }

  private log(args: z.infer<typeof schemas.log_time>) {
    const project = this.liveProject(args.projectId);
    if (!project) return { error: "Unknown project id" };
    if (args.taskId && this.dataset.tasks[args.taskId]?.projectId !== project.id) return { error: "That task does not belong to the project" };
    const day = localDate(args.date);
    const start = localTime(day, args.start);
    let end = localTime(day, args.end);
    if (end <= start) end += 24 * HOUR;
    if (end > this.now) return { error: "Time can only be logged up to now" };
    const overlap = overlappingEntries(this.dataset, start, end, this.now)[0];
    if (overlap) return { error: `Overlaps an entry on ${projectLabel(this.dataset, overlap.projectId)} from ${formatClock(overlap.start)} to ${overlap.end === null ? "now" : formatClock(overlap.end)}` };
    const operations = logEntry(this.dataset, { projectId: project.id, taskId: args.taskId ?? null, description: args.description ?? "" }, start, end, this.factory);
    return this.commit(operations, `Log ${formatDuration(end - start)} on ${projectLabel(this.dataset, project.id)}, ${args.date} ${formatClock(start)} to ${formatClock(end)}`, [operations[0]!.entityId]);
  }

  private start(args: z.infer<typeof schemas.start_timer>) {
    const project = this.liveProject(args.projectId);
    if (project === null) return { error: "Unknown project id" };
    const operations = startTimer(this.dataset, { projectId: project?.id ?? null, description: args.description ?? "" }, this.factory, this.now);
    return this.commit(operations, `Start a timer on ${projectLabel(this.dataset, project?.id ?? null)}`, [operations.at(-1)!.entityId]);
  }

  private stop() {
    const running = runningEntry(this.dataset);
    if (!running) return { error: "No timer is running" };
    return this.commit(stopTimer(this.dataset, this.factory, this.now), `Stop the timer on ${projectLabel(this.dataset, running.projectId)} at ${formatDuration(entryDuration(running, this.now))}`, [running.id]);
  }

  private entries(ids: string[]) {
    const entries = ids.map((id) => this.dataset.timeEntries[id]);
    if (entries.some((e) => !e || !isLive(e))) return { error: "Some entry ids do not exist" } as const;
    if (entries.some((e) => isEntryLocked(this.dataset, e!))) return { error: "Some entries are on a sent invoice and cannot change" } as const;
    return { entries: entries.map((e) => e!) } as const;
  }

  private update(args: z.infer<typeof schemas.update_entries>) {
    const found = this.entries(args.entryIds);
    if ("error" in found) return found;
    const project = args.projectId === undefined ? undefined : this.liveProject(args.projectId);
    if (project === null) return { error: "Unknown project id" };
    const operations = found.entries.map((entry) => {
      const patch: Record<string, unknown> = {};
      const projectId = project ? project.id : entry.projectId;
      if (project) { patch["projectId"] = project.id; patch["taskId"] = null; }
      if (args.description !== undefined) patch["description"] = args.description;
      const billable = canBeBillable(projectId ? this.dataset.projects[projectId] : undefined) && (args.billable ?? entry.billable);
      if (project || args.billable !== undefined) {
        patch["billable"] = billable;
        patch["rateSnapshot"] = rateSnapshotFor(this.dataset, projectId, billable);
      }
      return this.factory.op("timeEntry", entry.id, patch);
    });
    const what = [project ? `move to ${projectLabel(this.dataset, project.id)}` : "", args.description !== undefined ? `describe as "${args.description}"` : "", args.billable !== undefined ? (args.billable ? "mark billable" : "mark non-billable") : ""].filter(Boolean).join(", ");
    return this.commit(operations, `Update ${found.entries.length} ${found.entries.length === 1 ? "entry" : "entries"}: ${what || "no change"}`, found.entries.map((e) => e.id));
  }

  private remove(args: z.infer<typeof schemas.delete_entries>) {
    const found = this.entries(args.entryIds);
    if ("error" in found) return found;
    this.deletes += found.entries.length;
    const operations = found.entries.map((entry) => this.factory.op("timeEntry", entry.id, { deletedAt: this.now, ...(entry.end === null ? { end: this.now } : {}), ...(entry.invoiceId ? { invoiceId: null } : {}) }));
    return this.commit(operations, `Move ${found.entries.length} ${found.entries.length === 1 ? "entry" : "entries"} to Trash`, found.entries.map((e) => e.id));
  }

  private money(totals: MoneyByCurrency): string[] {
    return sortCurrencies(Object.keys(totals)).filter((currency) => totals[currency]).map((currency) => formatMoney({ amount: totals[currency]!, currency }));
  }

  private describeInvoice(invoice: Invoice) {
    const { totals, whole, installment } = invoiceDocument(this.dataset, invoice);
    return {
      id: invoice.id, number: invoice.number, client: this.dataset.clients[invoice.clientId]?.name ?? "Unknown client", status: invoice.status,
      overdue: isOverdue(invoice, this.now), period: `${isoDay(invoice.periodFrom)} to ${isoDay(invoice.periodTo)}`,
      issued: invoice.issueDate === null || invoice.status === "draft" ? null : isoDay(invoice.issueDate), due: invoice.dueDate === null || invoice.status === "draft" ? null : isoDay(invoice.dueDate),
      paid: invoice.paidAt === null ? null : isoDay(invoice.paidAt), total: formatMoney({ amount: totals.total, currency: invoice.currency }), beforeTax: formatMoney({ amount: totals.taxable, currency: invoice.currency }),
      installment: installment && { index: installment.index, of: installment.count, percent: installment.percent / 100, wholeInvoiceTotal: formatMoney({ amount: whole.total, currency: invoice.currency }) }
    };
  }

  private listInvoices(args: z.infer<typeof schemas.list_invoices>) {
    const invoices = findInvoices(this.dataset, this.now, args.status, args.clientId);
    this.saw({ kind: "invoices", ids: invoices.slice(0, 50).map((invoice) => invoice.id), total: invoices.length });
    return { total: invoices.length, invoices: invoices.slice(0, 50).map((invoice) => this.describeInvoice(invoice)), outstanding: this.money(outstandingByCurrency(this.dataset)), overdue: this.money(outstandingByCurrency(this.dataset, this.now)) };
  }

  private uninvoiced(args: z.infer<typeof schemas.uninvoiced_work>) {
    const source = uninvoicedWork(this.dataset, this.now, args.clientId);
    this.saw(source);
    return {
      clients: source.clients.map((row) => ({ clientId: row.clientId, client: row.name, uninvoiced: this.money(row.money), hours: formatDuration(row.ms), oldestDay: row.oldest === null ? null : isoDay(row.oldest) })),
      total: this.money(uninvoicedByCurrency(this.dataset))
    };
  }

  private listTasks(args: z.infer<typeof schemas.list_tasks>) {
    if (args.projectId && this.liveProject(args.projectId) === null) return { error: "Unknown project id" };
    const source = openTasks(this.dataset, this.now, args.projectId);
    this.saw({ ...source, items: source.items.slice(0, 100) });
    return {
      total: source.total,
      tasks: source.items.slice(0, 100).map((item) => {
        const project = this.dataset.projects[item.projectId]!;
        return {
          id: item.id, task: item.name, project: projectLabel(this.dataset, project.id), projectId: project.id, projectDeadline: project.deadline,
          logged: item.logged ? formatDuration(item.logged) : null, estimate: item.estimate === null ? null : formatDuration(item.estimate)
        };
      })
    };
  }

  private draftInvoice(args: z.infer<typeof schemas.draft_invoice>) {
    const client = this.dataset.clients[args.clientId];
    if (!client || !isLive(client)) return { error: "Unknown client id" };
    const currencies = clientCurrencies(this.dataset, client.id);
    const currency = args.currency?.toUpperCase() ?? (currencies.length <= 1 ? currencies[0] ?? this.dataset.settings.defaultCurrency : null);
    if (!currency) return { error: `${client.name} is billed in ${currencies.join(" and ")}. Ask which currency to invoice.` };
    if (currencies.length && !currencies.includes(currency)) return { error: `${client.name} has nothing billed in ${currency}` };
    const period = args.from && args.to ? { from: localDate(args.from), to: localDate(args.to) } : lastMonth(this.now);
    const id = makeId("invoice");
    const operations = createInvoiceDraft(this.dataset, { clientId: client.id, currency, periodFrom: period.from, periodTo: period.to }, this.factory, id);
    const next = applyOperations(this.dataset, operations);
    const { lines, totals } = invoiceDocument(next, next.invoices[id]!);
    if (!lines.length) return { error: `Nothing uninvoiced for ${client.name} in ${currency} from ${isoDay(period.from)} to ${isoDay(period.to)}` };
    this.confirm = true;
    return { ...this.commit(operations, `Draft an invoice for ${client.name}, ${formatPeriod(Math.min(period.from, period.to), Math.max(period.from, period.to))}: ${lines.length} ${lines.length === 1 ? "line" : "lines"}, ${formatMoney({ amount: totals.subtotal, currency })} before tax`, []), invoiceId: id };
  }

  private splitInvoice(args: z.infer<typeof schemas.split_invoice>) {
    const invoice = liveInvoice(this.dataset, args.invoiceId);
    if (!invoice) return { error: "Unknown invoice id" };
    const percents = args.percents.map((percent) => percent * 100);
    const error = installmentPlanError(this.dataset, invoice, percents);
    if (error) return { error };
    const { whole } = invoiceDocument(this.dataset, invoice);
    const client = this.dataset.clients[invoice.clientId]?.name ?? "the client";
    const change = percents.length === 1
      ? `Merge the installments for ${client} back into one invoice`
      : `Split the ${formatMoney({ amount: whole.total, currency: invoice.currency })} invoice for ${client} into ${percents.length} installments: ${args.percents.map((percent) => `${percent}%`).join(", ")}`;
    this.confirm = true;
    return this.commit(setInstallments(this.dataset, invoice, percents, this.now, this.factory), change, []);
  }

  private markSent(args: z.infer<typeof schemas.mark_invoice_sent>) {
    const invoice = liveInvoice(this.dataset, args.invoiceId);
    if (!invoice) return { error: "Unknown invoice id" };
    if (invoice.status !== "draft") return { error: `That invoice is already ${invoice.status}` };
    const { blockers } = sendChecks(this.dataset, invoice);
    if (blockers.length) return { error: blockers.join(" ") };
    const issue = args.issueDate ? localDate(args.issueDate) : startOfDay(this.now);
    const number = invoice.number ?? nextInvoiceNumber(this.dataset, new Date(issue).getFullYear()).number;
    const { totals } = invoiceDocument(this.dataset, invoice);
    this.confirm = true;
    return this.commit(sendInvoice(this.dataset, invoice, issue, this.factory), `Mark ${number} for ${this.dataset.clients[invoice.clientId]?.name ?? "the client"} as sent on ${isoDay(issue)}, ${formatMoney({ amount: totals.total, currency: invoice.currency })}. Its entries lock`, []);
  }

  private markPaid(args: z.infer<typeof schemas.mark_invoice_paid>) {
    const invoice = liveInvoice(this.dataset, args.invoiceId);
    if (!invoice) return { error: "Unknown invoice id" };
    if (invoice.status !== "sent") return { error: invoice.status === "paid" ? "That invoice is already paid" : "Only sent invoices can be marked paid" };
    const paidAt = args.paidDate ? localDate(args.paidDate) : startOfDay(this.now);
    if (paidAt > this.now) return { error: "The paid date cannot be in the future" };
    const { totals } = invoiceDocument(this.dataset, invoice);
    return this.commit(markInvoicePaid(invoice, paidAt, this.factory), `Mark ${invoice.number ?? "the invoice"} as paid on ${isoDay(paidAt)}, ${formatMoney({ amount: totals.total, currency: invoice.currency })}`, []);
  }
}

export async function runAssistant(text: string, dataset: Dataset, factory: OperationFactory, now: number, call: AssistantCall, onStep?: (step: AssistantStep) => void, google?: GoogleView): Promise<AssistantOutcome> {
  const runner = new ToolRunner(dataset, factory, now, google);
  const messages: ChatMessage[] = [
    { role: "system", content: buildSystemPrompt(assistantContext(dataset, now)) },
    { role: "user", content: text.slice(0, 4000) }
  ];
  let answer = "";
  for (let round = 0; round < MAX_ROUNDS; round += 1) {
    const response = await call({ messages });
    if (!response.toolCalls.length) {
      answer = response.content;
      break;
    }
    messages.push({ role: "assistant", content: response.content, toolCalls: response.toolCalls });
    for (const toolCall of response.toolCalls) {
      if (toolCall.name in schemas) onStep?.(toolCall.name as ToolName);
      const result = runner.run(toolCall.name, toolCall.arguments);
      messages.push({ role: "tool", toolCallId: toolCall.id, content: JSON.stringify(result).slice(0, 60_000) });
    }
    onStep?.("compose");
  }
  return {
    answer: answer || (runner.changes.length ? "" : "I could not work that out. Try rephrasing it."),
    sources: runner.sources,
    operations: runner.operations,
    changes: runner.changes,
    needsConfirmation: runner.confirm || runner.deletes > 0 || runner.touched.size > 1 || runner.mutationCalls > 1
  };
}
